import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';

// const BASE_URL = 'http://localhost:5000';
const BASE_URL = 'http://192.168.0.57:5000';

const TITLES   = ['Mr.', 'Mrs.', 'Ms.', 'Dr.'];
const WARN_OPTS = [30, 60, 90, 120, 180];

const EMPTY_FORM = {
  title: 'Mr.', first_name: '', middle_name: '', last_name: '',
  contact_no: '', email_id: '', dl_no: '', dl_expiredate: '',
  dateof_birth: '', address: '', city: '', state: '', zipcode: '',
  dl_warning_days: 90,
};

// ── DL Badge component ────────────────────────────────────────
function DLBadge({ status }) {
  if (!status) return null;
  const cfg = {
    valid:   { bg: '#f0fff6', color: '#1a9e5c', border: '#b8f0d0', icon: '✅' },
    warning: { bg: '#fffbf0', color: '#b45309', border: '#fcd34d', icon: '⚠️' },
    expired: { bg: '#fff0f0', color: '#c0392b', border: '#fca5a5', icon: '🚨' },
  }[status.status] || {};

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      animation: status.status === 'expired' ? 'blink 1s infinite' : 'none'
    }}>
      {cfg.icon} {status.label}
      <span style={{ fontWeight: 400 }}>({Math.abs(status.daysLeft)}d {status.daysLeft < 0 ? 'ago' : 'left'})</span>
    </span>
  );
}

export default function DriverManageDetails() {
  const navigate = useNavigate();

  // ── Table ──
  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Modal ──
  const [modalOpen,  setModalOpen]  = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [editId,     setEditId]     = useState(null);
  const [saving,     setSaving]     = useState(false);

  // ── Files ──
  const [files,    setFiles]    = useState({ driver_photo: null, dl_image: null, id_image: null });
  const [previews, setPreviews] = useState({ driver_photo: '', dl_image: '', id_image: '' });
  const photoRef  = useRef();
  const dlRef     = useRef();
  const idRef     = useRef();

  // ── View ──
  const [viewDriver, setViewDriver] = useState(null);

  // ── Auth ──
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/login');
  }, []);

  // ── Fetch ──
  const fetchData = useCallback((pg = page, q = search) => {
    setLoading(true);
    API.get('/drivers', { params: { page: pg, limit: 10, search: q } })
      .then(r => {
        setData(r.data.data);
        setTotalPages(r.data.totalPages);
        setTotal(r.data.total);
      })
      .catch(() => toast.error('Failed to load drivers'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(page, search); }, [page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(1, search); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Open Add ──
  const openAdd = () => {
    setForm(EMPTY_FORM); setEditId(null);
    setFiles({ driver_photo: null, dl_image: null, id_image: null });
    setPreviews({ driver_photo: '', dl_image: '', id_image: '' });
    setModalOpen(true);
  };

  // ── Open Edit ──
  const openEdit = (row) => {
    // Parse name parts from stored full name
    const parts = (row.driver_name || '').split(' ');
    const title = TITLES.includes(parts[0]) ? parts[0] : 'Mr.';
    const rest  = TITLES.includes(parts[0]) ? parts.slice(1) : parts;

    setForm({
      title,
      first_name:      rest[0]  || '',
      middle_name:     rest.length > 2 ? rest.slice(1, -1).join(' ') : '',
      last_name:       rest[rest.length - 1] || '',
      contact_no:      row.contact_no     || '',
      email_id:        row.email_id       || '',
      dl_no:           row.dl_no          || '',
      dl_expiredate:   row.dl_expiredate?.slice(0, 10) || '',
      dateof_birth:    row.dateof_birth?.slice(0, 10)  || '',
      address:         row.address        || '',
      city:            row.city           || '',
      state:           row.state          || '',
      zipcode:         row.zipcode        || '',
      dl_warning_days: row.dl_warning_days || 90,
    });
    setEditId(row.driver_id);
    setFiles({ driver_photo: null, dl_image: null, id_image: null });
    setPreviews({
      driver_photo: row.driver_photo ? `${BASE_URL}/${row.driver_photo}` : '',
      dl_image:     row.dl_image     ? `${BASE_URL}/${row.dl_image}`     : '',
      id_image:     row.id_image     ? `${BASE_URL}/${row.id_image}`     : '',
    });
    setModalOpen(true);
  };

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleFile = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    setFiles(f => ({ ...f, [field]: file }));
    setPreviews(p => ({ ...p, [field]: URL.createObjectURL(file) }));
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.first_name.trim())  { toast.warning('First name is required');      return; }
    if (!form.last_name.trim())   { toast.warning('Last name is required');       return; }
    if (!form.contact_no.trim())  { toast.warning('Contact number is required');  return; }
    if (!form.dl_no.trim())       { toast.warning('DL number is required');       return; }
    if (!form.dl_expiredate)      { toast.warning('DL expiry date is required');  return; }

    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (files.driver_photo) fd.append('driver_photo', files.driver_photo);
      if (files.dl_image)     fd.append('dl_image',     files.dl_image);
      if (files.id_image)     fd.append('id_image',     files.id_image);

      if (editId) {
        await API.put(`/drivers/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Driver updated successfully');
      } else {
        await API.post('/drivers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Driver added successfully');
      }
      setModalOpen(false);
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
    setSaving(false);
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this driver? This cannot be undone.')) return;
    try {
      await API.delete(`/drivers/${id}`);
      toast.success('Driver deleted successfully');
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  // ── File upload field ──
  const FileField = ({ label, field, inputRef }) => {
    const preview = previews[field];
    const hasNew  = !!files[field];
    const isPdf   = preview?.toLowerCase().endsWith('.pdf');
    return (
      <div style={S.fg}>
        <label style={S.label}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={S.fileBtn} onClick={() => inputRef.current.click()}>
            📎 {hasNew ? 'Change' : preview ? 'Replace' : 'Upload'}
          </button>
          <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.gif,.pdf"
            style={{ display: 'none' }} onChange={e => handleFile(e, field)} />
          {preview && !isPdf && (
            <img src={preview} alt={label}
              style={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }} />
          )}
          {preview && isPdf && (
            <a href={preview} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#6C63FF' }}>View PDF</a>
          )}
          {hasNew && <span style={{ fontSize: 11, color: '#1a9e5c' }}>✓ New file</span>}
        </div>
        <span style={{ fontSize: 11, color: '#aaa' }}>JPG / PNG / PDF, max 2MB</span>
      </div>
    );
  };

  return (
    <Layout activePage="Driver Details">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* blink keyframe */}
      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>

      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Driver Management</h2>
          <p style={S.subtitle}>Manage driver records and DL expiry tracking</p>
        </div>
        <button style={S.addBtn} onClick={openAdd}>➕ Add Driver</button>
      </div>

      {/* Search */}
      <div style={S.searchWrap}>
        <span style={{ fontSize: 14, color: '#aaa' }}>🔍</span>
        <input type="text" placeholder="Search by name, mobile, DL no or email..."
          value={search} onChange={e => setSearch(e.target.value)} style={S.searchInput} />
        {search && <button style={S.clearBtn} onClick={() => setSearch('')}>✕</button>}
      </div>

      {/* DL Legend */}
      <div style={S.legendRow}>
        {[
          { bg: '#f0fff6', color: '#1a9e5c', border: '#b8f0d0', label: '✅ Valid' },
          { bg: '#fffbf0', color: '#b45309', border: '#fcd34d', label: '⚠️ Expiring Soon' },
          { bg: '#fff0f0', color: '#c0392b', border: '#fca5a5', label: '🚨 Expired' },
        ].map(l => (
          <span key={l.label} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: l.bg, color: l.color, border: `1px solid ${l.border}` }}>
            {l.label}
          </span>
        ))}
        <span style={{ fontSize: 12, color: '#aaa' }}>DL Expiry Status</span>
      </div>

      {/* Table Card */}
      <div style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>#</th>
                <th style={S.th}>Photo</th>
                <th style={S.th}>Driver Name</th>
                <th style={S.th}>Contact</th>
                <th style={S.th}>DL Number</th>
                <th style={S.th}>DL Expiry</th>
                <th style={S.th}>City</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={S.emptyCell}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={8} style={S.emptyCell}>No records found</td></tr>
              ) : data.map((row, i) => (
                <tr key={row.driver_id} style={S.tr}>
                  <td style={S.td}><span style={S.serial}>{(page - 1) * 10 + i + 1}</span></td>

                  {/* Photo */}
                  <td style={S.td}>
                    {row.driver_photo
                      ? <img src={`${BASE_URL}/${row.driver_photo}`} alt={row.driver_name}
                          style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '50%',
                            border: '2px solid #e8e8e8' }} />
                      : <div style={S.avatarSm}>{row.driver_name?.[0]?.toUpperCase() || '?'}</div>}
                  </td>

                  {/* Name */}
                  <td style={S.td}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1d2e' }}>{row.driver_name}</div>
                    {row.email_id && <div style={{ fontSize: 11, color: '#aaa' }}>{row.email_id}</div>}
                  </td>

                  {/* Contact */}
                  <td style={S.td}>
                    <span style={S.mobileChip}>{row.contact_no}</span>
                  </td>

                  {/* DL No */}
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{row.dl_no}</td>

                  {/* DL Expiry with badge */}
                  <td style={S.td}>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                      {row.dl_expiredate
                        ? new Date(row.dl_expiredate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </div>
                    <DLBadge status={row.dl_status} />
                  </td>

                  {/* City */}
                  <td style={{ ...S.td, fontSize: 12, color: '#777' }}>{row.city || '—'}</td>

                  {/* Actions */}
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button style={S.viewBtn} onClick={() => setViewDriver(row)}>View</button>
                    <button style={S.editBtn} onClick={() => openEdit(row)}>Edit</button>
                    <button style={S.delBtn}  onClick={() => handleDelete(row.driver_id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={S.pagination}>
          <span style={S.paginInfo}>
            {total > 0 ? `Showing ${(page-1)*10+1}–${Math.min(page*10, total)} of ${total}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage(p => p-1)}>← Prev</button>
            <span style={S.paginInfo}>Page {page} / {totalPages || 1}</span>
            <button style={S.pgBtn} disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(p => p+1)}>Next →</button>
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalOpen && (
        <div style={S.overlay} onClick={() => !saving && setModalOpen(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                {editId ? '✏️ Edit Driver' : '➕ Add Driver'}
              </h3>
              <button style={S.closeBtn} onClick={() => !saving && setModalOpen(false)}>✕</button>
            </div>

            <div style={S.modalBody}>

              {/* Personal */}
              <div style={S.section}>Personal Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div style={S.fg}>
                  <label style={S.label}>Title</label>
                  <select name="title" value={form.title} onChange={handleChange} style={S.input}>
                    {TITLES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={S.fg}>
                  <label style={S.label}>First Name <span style={S.req}>*</span></label>
                  <input name="first_name" value={form.first_name} onChange={handleChange}
                    placeholder="First name" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Middle Name</label>
                  <input name="middle_name" value={form.middle_name} onChange={handleChange}
                    placeholder="Middle name" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Last Name <span style={S.req}>*</span></label>
                  <input name="last_name" value={form.last_name} onChange={handleChange}
                    placeholder="Last name" style={S.input} />
                </div>
              </div>

              <div style={S.grid2}>
                <div style={S.fg}>
                  <label style={S.label}>Contact No <span style={S.req}>*</span></label>
                  <input name="contact_no" value={form.contact_no} onChange={handleChange}
                    placeholder="Mobile number" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Email ID</label>
                  <input name="email_id" type="email" value={form.email_id} onChange={handleChange}
                    placeholder="email@example.com" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Date of Birth</label>
                  <input name="dateof_birth" type="date" value={form.dateof_birth}
                    onChange={handleChange} style={S.input} />
                </div>
              </div>

              {/* Address */}
              <div style={S.section}>Address</div>
              <div style={S.fg}>
                <label style={S.label}>Full Address</label>
                <textarea name="address" value={form.address} onChange={handleChange}
                  placeholder="Street address..." rows={2}
                  style={{ ...S.input, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12, marginBottom: 14 }}>
                <div style={S.fg}>
                  <label style={S.label}>City</label>
                  <input name="city" value={form.city} onChange={handleChange} placeholder="City" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>State</label>
                  <input name="state" value={form.state} onChange={handleChange} placeholder="State" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Zip Code</label>
                  <input name="zipcode" value={form.zipcode} onChange={handleChange} placeholder="Zip" style={S.input} />
                </div>
              </div>

              {/* DL */}
              <div style={S.section}>Driving Licence</div>
              <div style={S.grid2}>
                <div style={S.fg}>
                  <label style={S.label}>DL Number <span style={S.req}>*</span></label>
                  <input name="dl_no" value={form.dl_no} onChange={handleChange}
                    placeholder="DL number" style={{ ...S.input, fontFamily: 'monospace' }} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>DL Expiry Date <span style={S.req}>*</span></label>
                  <input name="dl_expiredate" type="date" value={form.dl_expiredate}
                    onChange={handleChange} style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Warn Before (days)</label>
                  <select name="dl_warning_days" value={form.dl_warning_days}
                    onChange={handleChange} style={S.input}>
                    {WARN_OPTS.map(d => <option key={d} value={d}>{d} days</option>)}
                  </select>
                </div>
              </div>

              {/* Uploads */}
              <div style={S.section}>Photos & Documents</div>
              <div style={S.grid2}>
                <FileField label="Driver Photo"    field="driver_photo" inputRef={photoRef} />
                <FileField label="DL Image / PDF"  field="dl_image"     inputRef={dlRef}    />
                <FileField label="ID Proof"        field="id_image"     inputRef={idRef}    />
              </div>

            </div>

            <div style={S.modalFoot}>
              <button style={S.cancelBtn} onClick={() => !saving && setModalOpen(false)}>Cancel</button>
              <button style={{ ...S.submitBtn, opacity: saving ? 0.7 : 1 }}
                onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Driver' : 'Add Driver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {viewDriver && (
        <div style={S.overlay} onClick={() => setViewDriver(null)}>
          <div style={{ ...S.modalBox, maxWidth: 540 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>🚗 {viewDriver.driver_name}</h3>
              <button style={S.closeBtn} onClick={() => setViewDriver(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              {/* Photo + DL status header */}
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 }}>
                {viewDriver.driver_photo
                  ? <img src={`${BASE_URL}/${viewDriver.driver_photo}`} alt={viewDriver.driver_name}
                      style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: '50%',
                        border: '3px solid #e8e8e8' }} />
                  : <div style={{ ...S.avatarSm, width: 72, height: 72, fontSize: 28 }}>
                      {viewDriver.driver_name?.[0]?.toUpperCase() || '?'}
                    </div>}
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1d2e' }}>{viewDriver.driver_name}</div>
                  <div style={{ fontSize: 13, color: '#999', marginBottom: 6 }}>{viewDriver.email_id}</div>
                  <DLBadge status={viewDriver.dl_status} />
                </div>
              </div>

              <div style={S.grid2}>
                {[
                  ['Contact',      viewDriver.contact_no],
                  ['DL Number',    viewDriver.dl_no],
                  ['DL Expiry',    viewDriver.dl_expiredate ? new Date(viewDriver.dl_expiredate).toLocaleDateString('en-IN') : '—'],
                  ['Date of Birth',viewDriver.dateof_birth  ? new Date(viewDriver.dateof_birth).toLocaleDateString('en-IN')  : '—'],
                  ['City',         viewDriver.city],
                  ['State',        viewDriver.state],
                  ['Zip Code',     viewDriver.zipcode],
                  ['Address',      viewDriver.address],
                ].map(([k, v]) => v ? (
                  <div key={k}>
                    <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase' }}>{k}</div>
                    <div style={{ fontSize: 13, color: '#333', fontWeight: 500, marginTop: 2 }}>{v}</div>
                  </div>
                ) : null)}
              </div>

              {/* Document links */}
              <div style={{ marginTop: 16 }}>
                <div style={S.section}>Documents</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {[
                    { path: viewDriver.dl_image,  label: 'DL Image'  },
                    { path: viewDriver.id_image,  label: 'ID Proof'  },
                  ].map(d => d.path ? (
                    <a key={d.label} href={`${BASE_URL}/${d.path}`} target="_blank" rel="noreferrer"
                      style={S.docBadge}>📄 {d.label}</a>
                  ) : null)}
                  {!viewDriver.dl_image && !viewDriver.id_image && (
                    <span style={{ color: '#bbb', fontSize: 13 }}>No documents uploaded</span>
                  )}
                </div>
              </div>
            </div>
            <div style={S.modalFoot}>
              <button style={S.submitBtn} onClick={() => setViewDriver(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ─── Styles ─── */
const S = {
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:       { margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
  subtitle:    { margin: '4px 0 0', fontSize: 13, color: '#999' },
  addBtn:      { padding: '10px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 14, fontWeight: 700, fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(108,99,255,0.35)' },

  searchWrap:  { display: 'flex', alignItems: 'center', gap: 8, background: '#fff',
    border: '1px solid #e8e8e8', borderRadius: 10, padding: '9px 16px',
    marginBottom: 14, maxWidth: 480, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  searchInput: { border: 'none', outline: 'none', fontSize: 13, flex: 1, fontFamily: 'inherit', color: '#333' },
  clearBtn:    { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 12, padding: 0 },

  legendRow:   { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' },

  card:        { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  theadRow:    { background: '#fafafa' },
  th:          { padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#999',
    textAlign: 'left', borderBottom: '2px solid #f0f0f0',
    textTransform: 'uppercase', letterSpacing: '0.5px' },
  tr:          { transition: 'background 0.12s' },
  td:          { padding: '12px 14px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell:   { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  serial:      { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  avatarSm:    { width: 44, height: 44, background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 800, fontSize: 16, flexShrink: 0 },
  mobileChip:  { background: '#f0f8ff', color: '#1a6eb5', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 },

  docBadge:    { padding: '6px 14px', background: '#f3f2ff', color: '#6C63FF',
    borderRadius: 20, fontSize: 13, fontWeight: 600,
    textDecoration: 'none', border: '1px solid #d4d0ff' },

  viewBtn:     { marginRight: 5, background: '#f0fff6', color: '#1a9e5c',
    border: '1px solid #b8f0d0', padding: '4px 12px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  editBtn:     { marginRight: 5, background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', padding: '4px 12px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  delBtn:      { background: '#fff2f5', color: '#FF6584', border: '1px solid #ffd0d8',
    padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },

  pagination:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderTop: '1px solid #f0f0f0' },
  paginInfo:   { fontSize: 12, color: '#999' },
  pgBtn:       { padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },

  overlay:     { position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.55)', zIndex: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' },
  modalBox:    { background: '#fff', borderRadius: 20, width: 740, maxWidth: '96vw', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' },
  modalHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 22px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  modalBody:   { padding: '20px 22px', overflowY: 'auto', flex: 1 },
  modalFoot:   { display: 'flex', justifyContent: 'flex-end', gap: 10,
    padding: '14px 22px', borderTop: '1px solid #f0f0f0', flexShrink: 0 },
  closeBtn:    { background: '#ff6b6b22', border: 'none', color: '#ff6b6b',
    borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontWeight: 800, fontSize: 14 },
  cancelBtn:   { padding: '9px 20px', background: '#f5f5f5', color: '#666',
    border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  submitBtn:   { padding: '9px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },

  section:     { fontSize: 11, fontWeight: 800, color: '#6C63FF', textTransform: 'uppercase',
    letterSpacing: '0.8px', margin: '16px 0 10px', paddingBottom: 6,
    borderBottom: '1.5px solid #f3f2ff' },
  grid2:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 },
  fg:          { display: 'flex', flexDirection: 'column', gap: 5 },
  label:       { fontSize: 12, fontWeight: 600, color: '#555' },
  req:         { color: '#ff6b6b' },
  input:       { padding: '9px 12px', border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    width: '100%', boxSizing: 'border-box' },
  fileBtn:     { padding: '7px 14px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
};
